import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { GlobalState } from 'app/global.state';
import { BaseComponent } from 'app/shared/infrastructure/base.component'; // leaf import - do not revert to the barrel (app/shared/infrastructure), breaks ng test (circular dependency)
import { Menu } from 'app/shared/models/control/menu';
import { PagesMenu } from 'app/pages/pages.menu';
import { layoutSizes } from 'app/shared/theme.constants';
import { MenuNavigationService } from 'app/shared/services/menu-navigation/menu-navigation.service';
import { MenuSearchService, FlatMenuEntry } from 'app/shared/services/menu-search/menu-search.service';
import { RecentUsageService } from 'app/shared/services/menu-recent-usage/recent-usage.service';
import { FavoritePageStaffService } from 'app/shared/services/core/favorite-page-staff/favorite-page-staff.service';
import { buildFavoritePayload } from 'app/shared/utils/menu-item-external.util';
import { AdvancedSearchBridgeService } from 'app/shared/services/core/advanced-search-bridge/advanced-search-bridge.service';
import {
    MenuKey, NO_OPEN_SCREENS, OpenScreens, OpenScreensService,
} from 'app/shared/services/menu-open-screens/open-screens.service';
import { SharedModulesMenuService } from 'app/shared/services/menu-shared-modules/shared-modules-menu.service';
import { SearchScope } from 'app/shared/controls/appShell/appShellFlyout/app-shell-flyout.component';

/**
 * Replaces pages.component.ts's <ba-sidebar>/<ba-page-top>/<ba-notification-favorite> shell.
 * Owns the shell-only UI state (active rail module, flyout open/closed, search query, active
 * screen) - all actual navigation/permission/business decisions still live in
 * MenuNavigationService/AuthGuard, this component only decides what to render.
 */
@Component({
    selector: 'app-shell',
    templateUrl: './app-shell.component.html',
    styleUrls: ['./app-shell.component.scss'],
})
export class AppShellComponent extends BaseComponent implements OnInit, OnDestroy {
    /** Mirrors pages.component.ts's `isHide` - the settlementDetail-route topbar-hide special case. */
    @Input() hideTopbar = false;

    /** The compile-time rail entries: every top-level PagesMenu module. */
    private readonly staticModules: Menu[] = PagesMenu.filter(m => m.isMain);
    /**
     * What the rail shows: the PagesMenu modules, plus a "Tools" module holding the shared
     * dynamic modules once their list answers (see loadToolsModule). Not readonly, and not just
     * PagesMenu: Tools is per-user and only known after an HTTP call, and it is left off the rail
     * entirely when the user may open none of them.
     */
    railModules: Menu[] = this.staticModules;

    activeModuleId: number | null = null;
    menuOpen = false;
    query = '';
    /**
     * Whether the flyout's search box and Pinned list cover the whole app or only the module
     * selected on the rail. Session state on purpose: it is a way of looking at the current list,
     * not a setting, and persisting it would leave a user who once narrowed the scope with a
     * silently half-empty Pinned list on their next visit. Defaults to the module: inside a
     * module that is the list the user is working in, and on Home there is nothing selected to
     * scope to, so scopeModuleId falls back to all modules on its own.
     */
    searchScope: SearchScope = 'module';
    /**
     * Which menu nodes are open as screens - drives the flyout's open/active row highlighting
     * and the sections tree's auto-expand. A screen opens in the primary router-outlet, one at a
     * time on this branch, so this holds at most one node: whichever screen the router is on
     * right now (see syncActiveScreen).
     */
    openScreens: OpenScreens = NO_OPEN_SCREENS;
    favorites: any[] = [];
    /** Whether some routed page's Advanced Search dialog is currently open - driven by
     * AdvancedSearchBridgeService.isOpen$ rather than the router, since AppShellComponent wraps
     * <router-outlet> and has no visibility into a nested page's own open/close state otherwise. */
    private advancedSearchOpen = false;
    private advancedSearchSub: Subscription;

    /** Topbar must stay hidden for either reason - the route-based settlementDetail case or an
     * open Advanced Search dialog - so it never renders on top of either. */
    get effectiveHideTopbar(): boolean {
        return this.hideTopbar || this.advancedSearchOpen;
    }
    /**
     * Whether the router is actually on the dashboard route right now. AppShellComponent wraps
     * <router-outlet> and is never destroyed/recreated on navigation, so activeModuleId alone
     * (only ever set by rail clicks) can't tell a fresh page load or a direct/favorite/search
     * navigation from an actual "Home" visit - without this, the pinned-favorites strip (see
     * showPinnedStrip) would render on top of whatever real screen the URL actually points at.
     */
    private isOnDashboard = true;

    constructor(
        private readonly state: GlobalState,
        private readonly router: Router,
        readonly navigation: MenuNavigationService,
        readonly menuSearch: MenuSearchService,
        private readonly recentUsage: RecentUsageService,
        private readonly favoriteService: FavoritePageStaffService,
        private readonly advancedSearchBridge: AdvancedSearchBridgeService,
        private readonly openScreensService: OpenScreensService,
        private readonly sharedModules: SharedModulesMenuService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.loadFavorites();
        this.loadToolsModule();
        // The shared-modules list is permission-filtered, and this route can activate before the
        // permissions exist: AuthService.setUser() assigns `user` (which is all AuthGuard checks)
        // and only then awaits setPermission(), so a shell built inside that window filters every
        // module out and would never look again. userChanged fires after setPermission() has
        // stored them, so ask once more then - it completes on that one emission, so this costs a
        // single extra call in the racy case and nothing at all when permissions were ready.
        this.authService.userChanged.subscribe(() => this.loadToolsModule());
        this.state.subscribe('favorites.changed', () => this.loadFavorites());
        if (this.shouldCollapse()) {
            this.setMenuOpen(false);
        }
        this.isOnDashboard = this.isDashboardUrl(this.router.url);
        this.syncActiveScreen(this.router.url);
        this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(e => {
                this.isOnDashboard = this.isDashboardUrl(e.urlAfterRedirects);
                this.syncActiveScreen(e.urlAfterRedirects);
            });
        this.advancedSearchSub = this.advancedSearchBridge.isOpen$.subscribe(isOpen => {
            this.advancedSearchOpen = isOpen;
        });
    }

    ngOnDestroy(): void {
        this.advancedSearchSub?.unsubscribe();
    }

    private isDashboardUrl(url: string): boolean {
        const path = url.split('?')[0].split('#')[0];
        return path === '/pages/dashboard' || path.startsWith('/pages/dashboard/');
    }

    /**
     * Points the sidebar at the screen the router just landed on: highlights its row and, through
     * OpenScreens.containerKeys, opens every section above it - so a screen reached from search,
     * a pinned favorite, the Home grid or a pasted url is as visibly "where I am" as one clicked
     * in the tree. Nothing here navigates; it only reflects a navigation that already happened.
     *
     * The rail has to follow the screen's own module as well, otherwise the tree holding the
     * highlighted row is not the tree being rendered and the highlight has nowhere to show. A url
     * behind no menu node (report viewer, not-authorized) clears the highlight rather than
     * leaving the previous screen lit - only one screen is open at a time here, so a row left
     * highlighted would be pointing at something no longer on screen. The rail keeps its module
     * in that case: that is where the user was browsing, and an unrecognized url says nothing
     * about wanting to leave it.
     */
    private syncActiveScreen(url: string): void {
        this.openScreens = this.openScreensService.resolveUrl(url);
        const moduleId = this.moduleIdOf(this.openScreens.activeKey);
        if (moduleId !== null) {
            this.activeModuleId = moduleId;
        }
    }

    /** The rail module a key belongs to - a key chain starts at its module's id (see MenuKey). */
    private moduleIdOf(key: MenuKey | null): number | null {
        if (!key) {
            return null;
        }
        const moduleId = Number(key.split('/')[0]);
        return this.railModules.some(m => m.id === moduleId) ? moduleId : null;
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.shouldCollapse() && this.menuOpen) {
            this.setMenuOpen(false);
        }
    }

    private shouldCollapse(): boolean {
        return window.innerWidth <= layoutSizes.resWidthCollapseSidebar;
    }

    get isHome(): boolean {
        return this.activeModuleId === null && this.isOnDashboard;
    }

    /**
     * The pinned-favorites strip stays up for the whole "I haven't opened anything yet" state -
     * browsing rail modules, expanding sections and typing in the search box all happen inside
     * the flyout column and leave the main area empty, so hiding the strip on `isHome`/`query`
     * (as this used to) blanked it out for no reason. It goes away only once a screen is really
     * open, i.e. the router left the dashboard for a routed screen.
     */
    get showPinnedStrip(): boolean {
        return this.isOnDashboard;
    }

    get activeModule(): Menu | undefined {
        return this.railModules.find(m => m.id === this.activeModuleId);
    }

    /** There is no module to scope to on Home - the rail has nothing selected. */
    get canScopeToModule(): boolean {
        return !!this.activeModule;
    }

    /**
     * The module the flyout's lists are limited to, or null for "all modules". Null whenever no
     * rail module is selected, so a stale `searchScope` from a module the user has since left
     * cannot silently empty the lists.
     */
    private get scopeModuleId(): number | null {
        return this.canScopeToModule && this.searchScope === 'module' ? this.activeModuleId : null;
    }

    get searchResults(): FlatMenuEntry[] {
        return this.menuSearch.searchRows(this.query, this.scopeModuleId);
    }

    /**
     * The user's favorites as flyout rows. Resolved through MenuSearchService rather than rendered
     * from the saved favorite records directly, so a pinned row carries the same breadcrumb and
     * module icon the same screen shows in search results and gets the same open/active
     * highlighting - a favorite record only stores Path/Title/Url.
     */
    get pinnedRows(): FlatMenuEntry[] {
        return this.menuSearch.rowsByPaths(this.pinnedPaths, this.scopeModuleId);
    }

    get pinnedPaths(): string[] {
        return this.favorites.map(f => f.Path);
    }

    setScope(scope: SearchScope): void {
        this.searchScope = scope;
    }

    goHome(): void {
        this.activeModuleId = null;
        this.query = '';
        this.setMenuOpen(false);
        // Navigate so the routed screen itself unmounts - without this, clicking Home only
        // reset the rail/flyout's own selection state while whatever feature page was routed
        // stayed mounted underneath the pinned-favorites strip.
        this.router.navigateByUrl('/pages/dashboard');
    }

    selectModule(id: number): void {
        this.activeModuleId = id;
        this.query = '';
        this.setMenuOpen(true);
    }

    setMenuOpen(open: boolean): void {
        this.menuOpen = open;
        this.state.notifyDataChanged('menu.isCollapsed', !open);
    }

    toggleMenuOpen(): void {
        this.setMenuOpen(!this.menuOpen);
    }

    onQueryChange(query: string): void {
        this.query = query;
    }

    activateItem(item: Menu): void {
        const filtered = this.navigation.filterChildren(item.children);
        const effective: Menu = { ...item, children: filtered } as Menu;
        const willNavigate = this.navigation.isNavigable(effective);
        this.navigation.navigate(effective);
        if (willNavigate) {
            this.recentUsage.recordVisit(effective.id);
            this.query = '';
        }
    }

    togglePinItem(payload: { item: Menu; event: Event }): void {
        const { item } = payload;
        const existing = this.favorites.find(f => f.Path === item.path);
        if (existing) {
            this.favoriteService.delete(existing.Id).subscribe(() => this.loadFavorites(), error => this.showError(error));
        } else {
            this.favoriteService.add(buildFavoritePayload(item)).subscribe(() => this.loadFavorites(), error => this.showError(error));
        }
    }

    openFavorite(favorite: any): void {
        if (favorite.IsExternalLink) {
            this.navigation.openLinkInNewTab(favorite.Url);
            return;
        }
        // Route through the real Menu node (found by its saved Path) so this goes through
        // MenuNavigationService.navigate() - the same open behaviour as clicking the screen from
        // the rail/flyout - instead of a favorite reimplementing it against its own saved Url.
        // Falls back to the saved Url for a favorite whose node is no longer in PagesMenu.
        const item = this.menuSearch.findByPath(favorite.Path);
        if (item) {
            this.navigation.navigate(item);
        } else {
            this.router.navigateByUrl(favorite.Url);
        }
        this.setMenuOpen(false);
    }

    unpinFavorite(payload: { favorite: any; event: Event }): void {
        this.favoriteService.delete(payload.favorite.Id).subscribe(() => this.loadFavorites(), error => this.showError(error));
    }

    /**
     * Puts the Tools module on the rail once the shared-modules list answers, and leaves the rail
     * as it is when there is nothing to show there - see SharedModulesMenuService, which also
     * explains why a failed call is a quiet null rather than an error toast. Not unsubscribed: an
     * HttpClient call completes on its own, and this component lives as long as the app does.
     */
    private loadToolsModule(): void {
        this.sharedModules.loadToolsModule().subscribe(tools => {
            if (!tools) {
                return;
            }
            // Registered before it goes on the rail so the search box and the Pinned list know
            // these screens from the moment their module is reachable.
            this.menuSearch.registerRuntimeModule(tools);
            this.railModules = [...this.staticModules, tools];
        });
    }

    private loadFavorites(): void {
        this.favoriteService.get().subscribe((data: any) => {
            this.favorites = Array.isArray(data) ? data : [];
        }, () => { this.favorites = []; });
    }
}
